// attempt-forensics-untracked-file-contents.ts
//
// Phase 06d R7 (F-17, B11): the post-route reset (`git clean -fd`) deletes the
// ONLY copy of files an attempt created (new tests, new modules) — the patch
// in attempt-forensics.ts covers tracked changes and only NAMES untracked
// files. This captures their contents, bounded:
//   - text file ≤ 64 KB and still inside the 512 KB total ⇒ `content`,
//     passed through the caller's redactor (SecretRedactor: registered values
//     + generic token patterns — the same pass the patch gets);
//   - larger, binary (NUL byte / invalid UTF-8) or over the total ⇒ name +
//     size + sha256 only (`omitted` says why);
//   - anything that is not a regular file (symlink, fifo…) ⇒ never opened.
//     A symlink planted by the worker could otherwise copy a file from
//     outside the worktree (e.g. `~/.avatar/secrets/…`) into the archive;
//     `O_NOFOLLOW` closes the lstat→open race as well. A hard link (nlink > 1,
//     read on the opened handle) can do the same ⇒ name + size only (L1);
//   - review M1: an un-ignored `venv/`/dataset must not stall the tick ⇒ at
//     most UNTRACKED_RECORD_MAX_COUNT records then ONE `count_cap` marker, and
//     no sha256 above UNTRACKED_HASH_MAX_BYTES (size only).
import { createHash } from "node:crypto";
import { constants, promises as fs } from "node:fs";
import { join } from "node:path";

export const UNTRACKED_FILE_MAX_BYTES = 64 * 1024;
export const UNTRACKED_TOTAL_MAX_BYTES = 512 * 1024;
export const UNTRACKED_RECORD_MAX_COUNT = 500;
export const UNTRACKED_HASH_MAX_BYTES = 16 * 1024 * 1024;

export type UntrackedOmittedReason =
  | "too_large"
  | "total_cap"
  | "binary"
  | "not_regular_file"
  | "hardlink"
  | "unreadable"
  | "count_cap";

export interface UntrackedFileRecord {
  path: string;
  size?: number;
  sha256?: string;
  content?: string;
  omitted?: UntrackedOmittedReason;
  /** `count_cap` marker only: files not recorded. */
  remaining?: number;
}

const CHUNK_BYTES = 64 * 1024;

async function sha256OfHandle(handle: fs.FileHandle): Promise<string> {
  const hash = createHash("sha256");
  const buffer = Buffer.alloc(CHUNK_BYTES);
  let position = 0;
  for (;;) {
    const { bytesRead } = await handle.read(buffer, 0, CHUNK_BYTES, position);
    if (bytesRead === 0) break;
    hash.update(buffer.subarray(0, bytesRead));
    position += bytesRead;
  }
  return hash.digest("hex");
}

function decodeText(bytes: Buffer): string | undefined {
  if (bytes.includes(0)) return undefined;
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return undefined;
  }
}

async function captureOne(
  repoDir: string,
  path: string,
  budget: number,
  redact: (text: string) => string,
): Promise<{ record: UntrackedFileRecord; used: number }> {
  const shownPath = redact(path);
  const abs = join(repoDir, path);
  const lst = await fs.lstat(abs);
  if (!lst.isFile()) return { record: { path: shownPath, omitted: "not_regular_file" }, used: 0 };
  const handle = await fs.open(abs, constants.O_RDONLY | constants.O_NOFOLLOW);
  try {
    const { size, nlink } = await handle.stat();
    if (nlink > 1) return { record: { path: shownPath, size, omitted: "hardlink" }, used: 0 };
    const tooLarge = size > UNTRACKED_FILE_MAX_BYTES;
    if (tooLarge || size > budget) {
      const omitted = tooLarge ? "too_large" : "total_cap";
      if (size > UNTRACKED_HASH_MAX_BYTES)
        return { record: { path: shownPath, size, omitted }, used: 0 };
      const sha256 = await sha256OfHandle(handle);
      return { record: { path: shownPath, size, sha256, omitted }, used: 0 };
    }
    const bytes = await handle.readFile();
    const sha256 = createHash("sha256").update(bytes).digest("hex");
    const text = decodeText(bytes);
    if (text === undefined) {
      return {
        record: { path: shownPath, size: bytes.length, sha256, omitted: "binary" },
        used: 0,
      };
    }
    return {
      record: { path: shownPath, size: bytes.length, sha256, content: redact(text) },
      used: bytes.length,
    };
  } finally {
    await handle.close();
  }
}

/** Contents of `paths` (relative to `repoDir`, from `git ls-files --others`), in order, within the caps. */
export async function captureUntrackedFileContents(
  repoDir: string,
  paths: readonly string[],
  redact: (text: string) => string,
): Promise<UntrackedFileRecord[]> {
  const records: UntrackedFileRecord[] = [];
  let budget = UNTRACKED_TOTAL_MAX_BYTES;
  for (const path of paths.slice(0, UNTRACKED_RECORD_MAX_COUNT)) {
    try {
      const { record, used } = await captureOne(repoDir, path, budget, redact);
      records.push(record);
      budget -= used;
    } catch {
      // Vanished / ELOOP / EACCES: keep the name so the archive still says it existed.
      records.push({ path: redact(path), omitted: "unreadable" });
    }
  }
  const remaining = paths.length - UNTRACKED_RECORD_MAX_COUNT;
  if (remaining > 0) {
    records.push({ path: `truncated: ${remaining} more`, omitted: "count_cap", remaining });
  }
  return records;
}
